import { get, writable, type Readable } from 'svelte/store';

export type BrowserCachedResourceStatus = 'unknown' | 'refreshing' | 'fresh' | 'stale' | 'error';

export type BrowserCachedResourceState<T> = {
  value: T;
  status: BrowserCachedResourceStatus;
  error: string | null;
  hasResolved: boolean;
  updatedAt: number | null;
};

type BrowserCachedResourceSnapshot<T> = {
  value: T;
  updatedAt: number;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type BrowserCachedResourceOptions<T> = {
  resourceKey: string;
  defaultValue: T;
  ttlMs: number;
  isBrowser: boolean;
  storage?: StorageLike;
  keepStaleUntilRefreshCompletes?: boolean;
  loadFresh: (scopeKey: string) => Promise<T>;
};

function buildStorageKey(resourceKey: string, scopeKey: string): string {
  return `cached-resource:${resourceKey}:${scopeKey}`;
}

export function createBrowserCachedResource<T>(options: BrowserCachedResourceOptions<T>): Readable<BrowserCachedResourceState<T>> & {
  load: (scopeKey: string | null, force?: boolean) => Promise<void>;
  reset: () => void;
  patch: (updater: (currentValue: T) => T) => void;
} {
  const storage = options.storage ?? (options.isBrowser ? localStorage : undefined);
  const initialState: BrowserCachedResourceState<T> = {
    value: options.defaultValue,
    status: 'unknown',
    error: null,
    hasResolved: false,
    updatedAt: null
  };

  const store = writable<BrowserCachedResourceState<T>>(initialState);

  let currentScopeKey: string | null = null;
  let inFlight: Promise<void> | null = null;
  let requestVersion = 0;

  function readSnapshot(scopeKey: string): BrowserCachedResourceSnapshot<T> | null {
    if (!storage) {
      return null;
    }

    const rawValue = storage.getItem(buildStorageKey(options.resourceKey, scopeKey));
    if (!rawValue) {
      return null;
    }

    try {
      const snapshot = JSON.parse(rawValue) as BrowserCachedResourceSnapshot<T>;
      if (typeof snapshot.updatedAt !== 'number') {
        storage.removeItem(buildStorageKey(options.resourceKey, scopeKey));
        return null;
      }

      return snapshot;
    } catch {
      storage.removeItem(buildStorageKey(options.resourceKey, scopeKey));
      return null;
    }
  }

  function persistSnapshot(scopeKey: string, snapshot: BrowserCachedResourceSnapshot<T>): void {
    if (!storage) {
      return;
    }

    storage.setItem(buildStorageKey(options.resourceKey, scopeKey), JSON.stringify(snapshot));
  }

  function hydrateSnapshot(scopeKey: string): BrowserCachedResourceSnapshot<T> | null {
    const snapshot = readSnapshot(scopeKey);
    if (!snapshot) {
      store.set(initialState);
      return null;
    }

    const isFresh = Date.now() - snapshot.updatedAt <= options.ttlMs;
    store.set({
      value: snapshot.value,
      status: isFresh ? 'fresh' : 'stale',
      error: null,
      hasResolved: true,
      updatedAt: snapshot.updatedAt
    });

    return snapshot;
  }

  function reset(): void {
    requestVersion += 1;
    currentScopeKey = null;
    inFlight = null;
    store.set(initialState);
  }

  function patch(updater: (currentValue: T) => T): void {
    store.update((current) => {
      const nextValue = updater(current.value);
      const nextUpdatedAt = Date.now();

      if (currentScopeKey) {
        persistSnapshot(currentScopeKey, { value: nextValue, updatedAt: nextUpdatedAt });
      }

      return {
        value: nextValue,
        status: current.hasResolved ? 'fresh' : current.status,
        error: null,
        hasResolved: current.hasResolved,
        updatedAt: current.hasResolved ? nextUpdatedAt : current.updatedAt
      };
    });
  }

  async function load(scopeKey: string | null, force = false): Promise<void> {
    if (!scopeKey) {
      reset();
      return;
    }

    if (scopeKey !== currentScopeKey) {
      currentScopeKey = scopeKey;
      inFlight = null;
      hydrateSnapshot(scopeKey);
    }

    const current = get(store);
    const isFresh = current.hasResolved
      && current.updatedAt !== null
      && Date.now() - current.updatedAt <= options.ttlMs
      && current.status === 'fresh';

    if (isFresh && !force) {
      return;
    }

    if (inFlight) {
      return inFlight;
    }

    const activeRequestVersion = ++requestVersion;
    store.update((state) => ({
      ...state,
      status: 'refreshing',
      error: null
    }));

    inFlight = (async () => {
      try {
        const freshValue = await options.loadFresh(scopeKey);
        if (activeRequestVersion !== requestVersion || currentScopeKey !== scopeKey) {
          return;
        }

        const updatedAt = Date.now();
        persistSnapshot(scopeKey, { value: freshValue, updatedAt });
        store.set({
          value: freshValue,
          status: 'fresh',
          error: null,
          hasResolved: true,
          updatedAt
        });
      } catch (error) {
        if (activeRequestVersion !== requestVersion || currentScopeKey !== scopeKey) {
          return;
        }

        const message = error instanceof Error ? error.message : 'Resource refresh failed.';
        const latest = get(store);

        if (latest.hasResolved && options.keepStaleUntilRefreshCompletes !== false) {
          store.set({
            ...latest,
            status: 'stale',
            error: message
          });
          return;
        }

        store.set({
          ...latest,
          status: 'error',
          error: message
        });
      } finally {
        if (activeRequestVersion === requestVersion) {
          inFlight = null;
        }
      }
    })();

    return inFlight;
  }

  return {
    subscribe: store.subscribe,
    load,
    reset,
    patch
  };
}